import axios from 'axios';
import { env } from '../env';

import { IAssistantResponse } from '@interfaces/assistantQuery';

const { REACT_APP_API_URL } = env;

function decodeUtf8FromAxiosData(data: unknown): string {
  if (typeof Buffer !== 'undefined' && Buffer.isBuffer(data)) {
    return data.toString('utf-8');
  }

  if (data instanceof ArrayBuffer) {
    const view = new Uint8Array(data);

    if (typeof Buffer !== 'undefined') {
      return Buffer.from(view).toString('utf-8');
    }

    return new TextDecoder('utf-8').decode(view);
  }

  if (ArrayBuffer.isView(data)) {
    const view = new Uint8Array(data.buffer, data.byteOffset, data.byteLength);

    if (typeof Buffer !== 'undefined') {
      return Buffer.from(view).toString('utf-8');
    }

    return new TextDecoder('utf-8').decode(view);
  }

  return '';
}

/**
 * Provides services for querying the assistant and handling responses.
 *
 * @layer service
 * @feature assistant-query
 * @uses axios - For making HTTP requests.
 * @uses env - Environment variables for API URLs.
 * @uses IAssistantResponse - Interface for the assistant response.
 */
export default class AssistantQueryServiceService {
  /**
   * Executes a query to retrieve assistant data based on the provided configuration.
   *
   * This method is used to query assistant-related data from a specified data source
   * using the provided configuration. It returns a promise that resolves to the query
   * results.
   *
   * @param {Object} [config] - The configuration object for the query. Defaults to an empty object.
   * @returns {Promise<any>} A promise that resolves to the query results.
   *
   * @uses Uses a data source to execute the query based on the provided configuration.
   */
  static async queryAssistant(config: any = {}): Promise<IAssistantResponse> {
    const { data } = await axios.post(
      `${REACT_APP_API_URL}api/v2/assistant/query/model`,
      {
        ...config,
      },
    );

    return data;
  }

  /**
   * Generates audio for an assistant based on the provided configuration.
   *
   * This method takes a configuration object and uses it to generate audio
   * suitable for an assistant. The configuration can include various
   * parameters such as voice type, language, and specific text to be spoken.
   *
   * @param {Object} [config={}] - The configuration object for generating the audio.
   *  - @param {string} [config.voiceType] - The type of voice to use for the audio.
   *  - @param {string} [config.language] - The language in which the text should be spoken.
   *  - @param {string} [config.text] - The text to be spoken in the generated audio.
   *
   * @returns {Promise<Buffer>} A promise that resolves to a Buffer containing the generated audio.
   *
   * @uses TextToSpeechService - Utilizes the TextToSpeechService to convert text to speech.
   */
  static async generateAssistantAudio(config: any = {}): Promise<ArrayBuffer> {
    const post = (extra: Record<string, unknown> = {}) =>
      axios.post(
        `${REACT_APP_API_URL}api/v2/assistant/query/audio`,
        {
          ...config,
          ...extra,
        },
        { responseType: 'arraybuffer' },
      );

    const response = await post();
    const contentType: string = String(
      response.headers['content-type'] || '',
    ).toLowerCase();

    if (contentType.includes('application/json')) {
      const json = JSON.parse(decodeUtf8FromAxiosData(response.data)) as {
        url?: string;
      };

      if (json.url) {
        const s3 = await fetch(json.url);

        if (s3.ok) {
          return await s3.arrayBuffer();
        }

        const retry = await post({ forceRefresh: true });

        return retry.data;
      }
    }

    return response.data;
  }

  /**
   * Flags an assistant's reply based on the provided configuration.
   *
   * This method is responsible for marking an assistant's reply as flagged
   * according to the settings provided in the configuration object. It may
   * involve updating a database, logging the event, or notifying relevant
   * parties.
   *
   * @param config - An object containing the configuration settings for
   *                 flagging the assistant's reply. Defaults to an empty object.
   *
   * @returns A promise that resolves when the flagging process is complete.
   *
   * @uses Database - May interact with a database to update the flagged status.
   * @uses Logger - May log the event of the assistant's reply being flagged.
   * @uses NotificationService - May notify relevant parties about the flagged reply.
   */
  static async flagAssistantReply(config: any = {}): Promise<any> {
    const { data } = await axios.post(
      `${REACT_APP_API_URL}api/v2/assistant/query/model/feedback`,
      {
        ...config,
      },
    );

    return data;
  }
}
